const contactEmail = "gabg.developer@gmail.com";

const legalLinks = {
  privacy: "/index.php/privacy-policies/",
  terms: "/index.php/terms-of-use/"
};

export const sharedLegal = {
  contactEmail,
  legalLinks,
  footer: {
    en: "© 2026 Gabg Dev. All rights reserved.",
    es: "© 2026 Gabg Dev. Todos los derechos reservados."
  },
  labels: {
    en: {
      brandTitle: "Gabg Dev",
      brandSubtitle: "Mobile applications legal",
      contact: "Contact us",
      language: "Language",
      privacy: "Privacy Policy",
      terms: "Terms of Use",
      lastUpdated: "Last updated: June 2026"
    },
    es: {
      brandTitle: "Gabg Dev",
      brandSubtitle: "Legal de aplicaciones móviles",
      contact: "Contacto",
      language: "Idioma",
      privacy: "Política de Privacidad",
      terms: "Términos de Uso",
      lastUpdated: "Última actualización: Junio 2026"
    }
  }
};

export const privacyPolicyPage = {
  key: "privacy",
  icon: "fa-shield",
  title: {
    en: "Privacy Policy | Gabg Dev",
    es: "Política de Privacidad | Gabg Dev"
  },
  description: {
    en: "Privacy Policy for mobile applications published by Gabg Dev.",
    es: "Política de Privacidad para aplicaciones móviles publicadas por Gabg Dev."
  },
  content: {
    en: {
      hero: {
        kicker: "Privacy Policy",
        title: "Clear privacy practices for applications published by Gabg Dev.",
        copy:
          "This Privacy Policy explains how mobile applications published by Gabg Dev may handle information, application analytics, feedback, third-party services, and user choices.",
        panelTitle: "Built for mobile application users",
        panelCopy:
          "We aim to collect only what is needed to operate, improve, protect, and support our applications. We do not include a physical address or unnecessary sensitive details in this policy."
      },
      nav: [
        { href: "#overview", label: "Overview" },
        { href: "#information", label: "Information" },
        { href: "#use", label: "Use" },
        { href: "#permissions", label: "Permissions" },
        { href: "#third-party", label: "Third-Party Services" },
        { href: "#rights", label: "Your Rights" },
        { href: "#contact", label: "Contact" }
      ],
      sections: [
        {
          id: "information",
          eyebrow: "Information we handle",
          title: "Information We Collect",
          paragraphs: [
            "Gabg Dev does not intentionally collect sensitive personal information such as physical addresses, private files, photos, videos, contacts, calendar entries, or payment card details through its mobile applications.",
            "Some applications may collect or process limited non-personal or usage-related information to keep the applications working, understand performance, improve features, prevent abuse, and respond to user feedback."
          ],
          grid: [
            {
              icon: "fa-mobile",
              title: "Non-Personal Information",
              copy:
                "This may include device type, operating system, application version, language, general technical diagnostics, approximate usage patterns, or other information that does not directly identify you."
            },
            {
              icon: "fa-line-chart",
              title: "Usage Analytics",
              copy:
                "Some applications may collect non-sensitive application events to understand which screens and features are used and to improve the product experience."
            },
            {
              icon: "fa-bug",
              title: "Error Reports",
              copy:
                "Crash reports and error logs may be processed to diagnose bugs, improve reliability, and protect application functionality."
            },
            {
              icon: "fa-commenting-o",
              title: "User Feedback",
              copy:
                "If you voluntarily send feedback, support requests, suggestions, or reports, we may process the information you choose to provide."
            }
          ]
        },
        {
          eyebrow: "Application events",
          title: "Non-Sensitive Usage Events",
          paragraphs: [
            "Our applications may collect non-sensitive usage events. These events help us understand application behavior and improve content, navigation, stability, and feature quality."
          ],
          checkList: [
            "Screens visited",
            "Features used",
            "Sharing events",
            "Crash and error reports",
            "Suggestions or feedback sent by the user"
          ]
        },
        {
          id: "use",
          eyebrow: "Purpose and limits",
          title: "How We Use Information",
          paragraphs: ["Information may be used to:"],
          bullets: [
            "Operate, maintain, and improve our mobile applications.",
            "Understand application usage and prioritize product improvements.",
            "Analyze crashes, errors, performance, and reliability issues.",
            "Respond to feedback, reports, suggestions, and support requests.",
            "Display or measure third-party ads where ads are included.",
            "Protect application functionality, detect abuse, and maintain security."
          ],
          afterParagraphs: [
            "We do not sell personal information. We also do not combine non-personal application analytics with sensitive personal data."
          ]
        },
        {
          id: "permissions",
          eyebrow: "Device access",
          title: "Permissions",
          paragraphs: [
            "Applications published by Gabg Dev may request permissions needed for normal application functionality, such as Internet access, network connectivity, content sharing, and opening external links.",
            "These permissions are used only for their intended purpose within the application experience. They are never used to collect unnecessary personal information or access data that is not needed for the feature being used."
          ]
        },
        {
          id: "third-party",
          eyebrow: "Partners and platforms",
          title: "Third-Party Services",
          paragraphs: [
            "Our applications may use third-party services that process information according to their own privacy policies, terms, and technical systems."
          ],
          services: [
            {
              title: "Google AdMob",
              copy:
                "Some applications may display ads provided by Google AdMob. AdMob may process device, advertising, usage, and ad interaction data according to Google's own policies."
            },
            {
              title: "PayPal",
              copy:
                "If a donation option is available, payments are handled externally by PayPal. Gabg Dev does not receive or store full payment card details."
            },
            {
              title: "Apple App Store / Google Play",
              copy:
                "When you install, update, subscribe, purchase, review, or interact with an application through an app store, Apple or Google may process information under their own terms and privacy policies."
            },
            {
              title: "AWS",
              copy:
                "Some applications or backend features may use AWS for hosting, storage, infrastructure, logs, or related backend services."
            }
          ]
        },
        {
          eyebrow: "External destinations",
          title: "Ads, Donations, and External Links",
          paragraphs: [
            "Some applications may include third-party ads, external links, sharing features, donation links, app store links, or other destinations outside our control. If you open or interact with those services, the information you provide is governed by the privacy policy and terms of that external service.",
            "Donations, when available, are processed outside our applications through PayPal. Gabg Dev does not process or store your full payment details."
          ]
        },
        {
          eyebrow: "Protection and lifecycle",
          title: "Security and Data Retention",
          paragraphs: [
            "We use reasonable administrative, technical, and organizational measures designed to protect information handled through our applications. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.",
            "We retain information only for as long as reasonably necessary for the purposes described in this policy, such as application operation, analytics, support, security, legal compliance, or resolving technical issues. Retention periods may vary depending on the application, service provider, and type of information."
          ]
        },
        {
          id: "rights",
          eyebrow: "Control and transparency",
          title: "User Rights and Choices",
          paragraphs: [
            "Depending on your location and applicable law, you may have rights to request access, correction, deletion, restriction, or information about data associated with you. You may also control certain permissions and advertising settings through your device, operating system, app store account, or third-party service settings.",
            "To submit a privacy request, contact us using the email below. We may need enough information to verify and process your request."
          ]
        },
        {
          eyebrow: "Updates",
          title: "Changes to This Policy",
          paragraphs: [
            "We may update this Privacy Policy from time to time to reflect changes in our applications, third-party services, legal requirements, or privacy practices. When we update the policy, we will revise the \"Last updated\" date at the top of this page."
          ]
        }
      ],
      contact: {
        eyebrow: "Contact",
        title: "Questions about this Privacy Policy?",
        copy:
          "Contact us for privacy questions, requests, feedback, or reports related to applications published by Gabg Dev."
      }
    },
    es: {
      hero: {
        kicker: "Política de Privacidad",
        title: "Prácticas de privacidad claras para aplicaciones publicadas por Gabg Dev.",
        copy:
          "Esta Política de Privacidad explica cómo las aplicaciones móviles publicadas por Gabg Dev pueden manejar información, analíticas de uso, comentarios, servicios de terceros y opciones del usuario.",
        panelTitle: "Pensada para usuarios de aplicaciones móviles",
        panelCopy:
          "Buscamos recopilar solo lo necesario para operar, mejorar, proteger y dar soporte a nuestras aplicaciones. No incluimos dirección física ni detalles sensibles innecesarios en esta política."
      },
      nav: [
        { href: "#overview", label: "Resumen" },
        { href: "#information", label: "Información" },
        { href: "#use", label: "Uso" },
        { href: "#permissions", label: "Permisos" },
        { href: "#third-party", label: "Servicios de Terceros" },
        { href: "#rights", label: "Derechos" },
        { href: "#contact", label: "Contacto" }
      ],
      sections: [
        {
          id: "information",
          eyebrow: "Información que manejamos",
          title: "Información que Recopilamos",
          paragraphs: [
            "Gabg Dev no recopila intencionalmente información personal sensible como direcciones físicas, archivos privados, fotos, videos, contactos, entradas de calendario o datos completos de tarjetas de pago a través de sus aplicaciones móviles.",
            "Algunas aplicaciones pueden recopilar o procesar información limitada no personal o relacionada con el uso para mantener las aplicaciones funcionando, entender el rendimiento, mejorar funciones, prevenir abuso y responder comentarios de usuarios."
          ],
          grid: [
            {
              icon: "fa-mobile",
              title: "Información No Personal",
              copy:
                "Esto puede incluir tipo de dispositivo, sistema operativo, versión de la aplicación, idioma, diagnósticos técnicos generales, patrones aproximados de uso u otra información que no te identifica directamente."
            },
            {
              icon: "fa-line-chart",
              title: "Analíticas de Uso",
              copy:
                "Algunas aplicaciones pueden recopilar eventos no sensibles para entender qué pantallas y funciones se usan y mejorar la experiencia del producto."
            },
            {
              icon: "fa-bug",
              title: "Reportes de Errores",
              copy:
                "Los reportes de fallos y registros de errores pueden procesarse para diagnosticar problemas, mejorar la confiabilidad y proteger la funcionalidad de la aplicación."
            },
            {
              icon: "fa-commenting-o",
              title: "Comentarios del Usuario",
              copy:
                "Si envías comentarios, solicitudes de soporte, sugerencias o reportes voluntariamente, podemos procesar la información que decidas proporcionar."
            }
          ]
        },
        {
          eyebrow: "Eventos de aplicación",
          title: "Eventos de Uso No Sensibles",
          paragraphs: [
            "Nuestras aplicaciones pueden recopilar eventos de uso no sensibles. Estos eventos nos ayudan a entender el comportamiento de la aplicación y mejorar contenido, navegación, estabilidad y calidad de funciones."
          ],
          checkList: [
            "Pantallas visitadas",
            "Funciones utilizadas",
            "Eventos de compartir",
            "Reportes de errores",
            "Sugerencias o comentarios enviados por el usuario"
          ]
        },
        {
          id: "use",
          eyebrow: "Propósito y límites",
          title: "Cómo Usamos la Información",
          paragraphs: ["La información puede usarse para:"],
          bullets: [
            "Operar, mantener y mejorar nuestras aplicaciones móviles.",
            "Entender el uso de la aplicación y priorizar mejoras del producto.",
            "Analizar fallos, errores, rendimiento y problemas de confiabilidad.",
            "Responder comentarios, reportes, sugerencias y solicitudes de soporte.",
            "Mostrar o medir anuncios de terceros cuando la aplicación incluya anuncios.",
            "Proteger la funcionalidad de la aplicación, detectar abuso y mantener la seguridad."
          ],
          afterParagraphs: [
            "No vendemos información personal. Tampoco combinamos analíticas no personales de la aplicación con datos personales sensibles."
          ]
        },
        {
          id: "permissions",
          eyebrow: "Acceso del dispositivo",
          title: "Permisos",
          paragraphs: [
            "Las aplicaciones publicadas por Gabg Dev pueden solicitar permisos necesarios para la funcionalidad normal de la aplicación, como acceso a Internet, conectividad de red, compartir contenido y abrir enlaces externos.",
            "Estos permisos se usan solo para su propósito previsto dentro de la experiencia de la aplicación. Nunca se usan para recopilar información personal innecesaria ni acceder a datos que no sean necesarios para la función utilizada."
          ]
        },
        {
          id: "third-party",
          eyebrow: "Socios y plataformas",
          title: "Servicios de Terceros",
          paragraphs: [
            "Nuestras aplicaciones pueden usar servicios de terceros que procesan información de acuerdo con sus propias políticas de privacidad, términos y sistemas técnicos."
          ],
          services: [
            {
              title: "Google AdMob",
              copy:
                "Algunas aplicaciones pueden mostrar anuncios proporcionados por Google AdMob. AdMob puede procesar datos del dispositivo, publicidad, uso e interacción con anuncios según las políticas de Google."
            },
            {
              title: "PayPal",
              copy:
                "Si hay una opción de donación disponible, los pagos se manejan externamente por PayPal. Gabg Dev no recibe ni almacena datos completos de tarjetas de pago."
            },
            {
              title: "Apple App Store / Google Play",
              copy:
                "Cuando instalas, actualizas, compras, reseñas o interactúas con una aplicación a través de una tienda, Apple o Google pueden procesar información bajo sus propios términos y políticas de privacidad."
            },
            {
              title: "AWS",
              copy:
                "Algunas aplicaciones o funciones backend pueden usar AWS para hosting, almacenamiento, infraestructura, logs o servicios backend relacionados."
            }
          ]
        },
        {
          eyebrow: "Destinos externos",
          title: "Anuncios, Donaciones y Enlaces Externos",
          paragraphs: [
            "Algunas aplicaciones pueden incluir anuncios de terceros, enlaces externos, funciones para compartir, enlaces de donación, enlaces a tiendas de aplicaciones u otros destinos fuera de nuestro control. Si abres o interactúas con esos servicios, la información que proporciones se rige por la política de privacidad y términos de ese servicio externo.",
            "Las donaciones, cuando están disponibles, se procesan fuera de nuestras aplicaciones mediante PayPal. Gabg Dev no procesa ni almacena tus datos completos de pago."
          ]
        },
        {
          eyebrow: "Proteccion y ciclo de vida",
          title: "Seguridad y Retencion de Datos",
          paragraphs: [
            "Usamos medidas administrativas, técnicas y organizativas razonables para proteger la información manejada por nuestras aplicaciones. Ningún método de transmisión o almacenamiento es completamente seguro, por lo que no podemos garantizar seguridad absoluta.",
            "Retenemos información solo durante el tiempo razonablemente necesario para los fines descritos en esta política, como operación de la aplicación, analíticas, soporte, seguridad, cumplimiento legal o resolución de problemas técnicos."
          ]
        },
        {
          id: "rights",
          eyebrow: "Control y transparencia",
          title: "Derechos y Opciones del Usuario",
          paragraphs: [
            "Dependiendo de tu ubicación y la ley aplicable, puedes tener derecho a solicitar acceso, corrección, eliminación, restricción o información sobre datos asociados contigo. También puedes controlar ciertos permisos y configuraciones de publicidad desde tu dispositivo, sistema operativo, cuenta de tienda de aplicaciones o servicios de terceros.",
            "Para enviar una solicitud de privacidad, contáctanos usando el email indicado abajo. Podemos necesitar información suficiente para verificar y procesar tu solicitud."
          ]
        },
        {
          eyebrow: "Actualizaciones",
          title: "Cambios a Esta Politica",
          paragraphs: [
            "Podemos actualizar esta Política de Privacidad ocasionalmente para reflejar cambios en nuestras aplicaciones, servicios de terceros, requisitos legales o prácticas de privacidad. Cuando actualicemos la política, revisaremos la fecha de última actualización en esta página."
          ]
        }
      ],
      contact: {
        eyebrow: "Contacto",
        title: "¿Preguntas sobre esta Política de Privacidad?",
        copy:
          "Contáctanos para preguntas de privacidad, solicitudes, comentarios o reportes relacionados con aplicaciones publicadas por Gabg Dev."
      }
    }
  }
};

export const termsOfUsePage = {
  key: "terms",
  icon: "fa-file-text-o",
  title: {
    en: "Terms of Use | Gabg Dev",
    es: "Términos de Uso | Gabg Dev"
  },
  description: {
    en: "Terms for using mobile applications published by Gabg Dev.",
    es: "Términos para el uso de aplicaciones móviles publicadas por Gabg Dev."
  },
  content: {
    en: {
      hero: {
        kicker: "Terms of Use",
        title: "Terms of Use",
        copy:
          "Terms for using mobile applications published by Gabg Dev.",
        panelTitle: "For Gabg Dev mobile applications",
        panelCopy:
          "These terms cover normal application use, app store distribution, optional donations, advertising, analytics, third-party services, and mobile application features."
      },
      nav: [
        { href: "#acceptance", label: "Acceptance" },
        { href: "#about", label: "About" },
        { href: "#intended-use", label: "Intended Use" },
        { href: "#responsibilities", label: "Responsibilities" },
        { href: "#intellectual-property", label: "IP" },
        { href: "#third-party-content", label: "Third-Party Content" },
        { href: "#payments", label: "Payments" },
        { href: "#ads", label: "Ads" },
        { href: "#analytics", label: "Analytics" },
        { href: "#external-links", label: "External Links" },
        { href: "#platform-rules", label: "Platforms" },
        { href: "#availability", label: "Availability" },
        { href: "#disclaimer", label: "Disclaimer" },
        { href: "#liability", label: "Liability" },
        { href: "#termination", label: "Termination" },
        { href: "#changes", label: "Changes" },
        { href: "#contact", label: "Contact" }
      ],
      sections: [
        {
          id: "acceptance",
          eyebrow: "Agreement",
          title: "Acceptance of Terms",
          paragraphs: [
            "By downloading, installing, accessing, or using any mobile application published by Gabg Dev, you agree to these Terms of Use.",
            "If you do not agree with these Terms, please do not use the applications."
          ]
        },
        {
          id: "about",
          eyebrow: "Our applications",
          title: "About Our Applications",
          paragraphs: [
            "Gabg Dev publishes mobile applications designed for personal, educational, productivity, entertainment, informational, and other user experiences.",
            "These Terms are intended to apply to all current and future mobile applications published by Gabg Dev, unless a specific application provides separate terms."
          ]
        },
        {
          id: "intended-use",
          eyebrow: "Allowed use",
          title: "Intended Use",
          paragraphs: [
            "Our applications are provided for lawful and personal use.",
            "You agree to use the applications responsibly and not to misuse, disrupt, modify, copy, reverse engineer, or interfere with the normal operation of the applications or related services."
          ]
        },
        {
          id: "responsibilities",
          eyebrow: "User conduct",
          title: "User Responsibilities",
          paragraphs: [
            "You are responsible for how you use the applications and for any information you voluntarily submit through features such as feedback forms, issue reports, suggestions, or support requests.",
            "You agree not to submit unlawful, abusive, misleading, harmful, or offensive content."
          ]
        },
        {
          id: "intellectual-property",
          eyebrow: "Ownership",
          title: "Intellectual Property",
          paragraphs: [
            "All application names, designs, layouts, branding, source code, graphics, interfaces, and related materials are owned by Gabg Dev or their respective owners.",
            "You may not copy, distribute, sell, modify, or reuse any part of the applications without permission, except where allowed by law or by the app stores' terms."
          ]
        },
        {
          id: "third-party-content",
          eyebrow: "Third-party content",
          title: "Application and Third-Party Content",
          paragraphs: [
            "Some applications may display text, audio, media, references, data, or other content that may be owned or licensed by third parties.",
            "Such content remains the property of its respective owners and may be subject to separate copyright or licensing terms.",
            "Users should not redistribute third-party content from the applications unless permitted by the applicable copyright holder, license, or platform terms."
          ]
        },
        {
          id: "payments",
          eyebrow: "Voluntary support",
          title: "Donations and Payments",
          paragraphs: [
            "Some applications may include voluntary donation options, such as donations through PayPal or other payment providers.",
            "Donations are optional and are intended to support development, hosting, maintenance, and future improvements.",
            "Unless clearly stated otherwise, donations:"
          ],
          bullets: [
            "do not provide ownership rights;",
            "do not unlock guaranteed premium features;",
            "do not create a subscription;",
            "do not guarantee future features;",
            "are generally non-refundable."
          ],
          afterParagraphs: [
            "Payment information is processed by third-party payment providers. Gabg Dev does not store full credit card or debit card information."
          ]
        },
        {
          id: "ads",
          eyebrow: "Advertising",
          title: "Advertisements",
          paragraphs: [
            "Some applications may display advertisements through Google AdMob or similar advertising services.",
            "Ad content, personalization, measurement, and ad interaction may be handled by third-party advertising providers according to their own terms and privacy policies."
          ]
        },
        {
          id: "analytics",
          eyebrow: "Application improvement",
          title: "Analytics and Application Improvement",
          paragraphs: [
            "Some applications may collect non-sensitive usage events to understand how features are used, improve performance, detect errors, and prioritize future improvements.",
            "Examples may include:"
          ],
          bullets: [
            "screens visited;",
            "features used;",
            "sharing actions;",
            "audio usage;",
            "feedback sent;",
            "crash or error reports."
          ],
          afterParagraphs: [
            "This information is used to improve the application experience and is handled according to our Privacy Policy."
          ]
        },
        {
          id: "external-links",
          eyebrow: "Third parties",
          title: "External Links and Third-Party Services",
          paragraphs: [
            "Our applications may contain links to external websites or services, including PayPal, Google, Apple, AWS, app stores, advertising providers, or other third-party platforms.",
            "Gabg Dev is not responsible for the content, policies, availability, or practices of third-party services.",
            "Your use of third-party services is subject to their own terms and privacy policies."
          ]
        },
        {
          id: "platform-rules",
          eyebrow: "Distribution",
          title: "App Store and Platform Rules",
          paragraphs: [
            "When downloading or using our applications through Apple App Store, Google Play, or other app distribution platforms, you also agree to the applicable rules and terms of those platforms."
          ]
        },
        {
          id: "availability",
          eyebrow: "Operation and updates",
          title: "Availability and Changes",
          paragraphs: [
            "We may update, modify, suspend, or discontinue any application or feature at any time.",
            "We do not guarantee that applications will always be available, error-free, uninterrupted, or compatible with every device or operating system version."
          ]
        },
        {
          id: "disclaimer",
          eyebrow: "No warranties",
          title: "Disclaimer",
          paragraphs: [
            "The applications are provided “as is” and “as available.”",
            "To the maximum extent permitted by law, Gabg Dev makes no warranties regarding the availability, accuracy, reliability, completeness, or suitability of the applications.",
            "Applications published by Gabg Dev may include educational, informational, productivity, entertainment, or other application content. They are not a substitute for professional, legal, medical, psychological, financial, or other qualified advice."
          ]
        },
        {
          id: "liability",
          eyebrow: "Legal limits",
          title: "Limitation of Liability",
          paragraphs: [
            "To the maximum extent permitted by applicable law, Gabg Dev shall not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the applications.",
            "This includes, but is not limited to, data loss, device issues, service interruptions, lost profits, or inability to access features."
          ]
        },
        {
          id: "termination",
          eyebrow: "Access restrictions",
          title: "Termination",
          paragraphs: [
            "We may restrict or discontinue access to certain features if we believe a user is abusing the application, violating these Terms, attempting unauthorized access, or interfering with the service."
          ]
        },
        {
          id: "changes",
          eyebrow: "Updates",
          title: "Changes to These Terms",
          paragraphs: [
            "We may update these Terms of Use from time to time.",
            "When we update them, we will revise the “Last updated” date on this page.",
            "Continued use of the applications after changes means you accept the updated Terms."
          ]
        },
        {
          id: "contact",
          eyebrow: "Contact",
          title: "Contact",
          paragraphs: [
            "If you have questions about these Terms of Use, contact us at:",
            contactEmail
          ]
        }
      ],
      contact: null
    },
    es: {
      hero: {
        kicker: "Términos de Uso",
        title: "Términos de Uso",
        copy:
          "Términos para el uso de aplicaciones móviles publicadas por Gabg Dev.",
        panelTitle: "Para aplicaciones móviles de Gabg Dev",
        panelCopy:
          "Estos términos cubren el uso normal de las aplicaciones, distribución en tiendas, donaciones opcionales, anuncios, analíticas, servicios de terceros y funciones móviles."
      },
      nav: [
        { href: "#acceptance", label: "Aceptación" },
        { href: "#about", label: "Sobre Aplicaciones" },
        { href: "#intended-use", label: "Uso Permitido" },
        { href: "#responsibilities", label: "Responsabilidades" },
        { href: "#intellectual-property", label: "Propiedad" },
        { href: "#third-party-content", label: "Contenido de Terceros" },
        { href: "#payments", label: "Pagos" },
        { href: "#ads", label: "Anuncios" },
        { href: "#analytics", label: "Analíticas" },
        { href: "#external-links", label: "Enlaces Externos" },
        { href: "#platform-rules", label: "Plataformas" },
        { href: "#availability", label: "Disponibilidad" },
        { href: "#disclaimer", label: "Descargo" },
        { href: "#liability", label: "Limitación" },
        { href: "#termination", label: "Terminación" },
        { href: "#changes", label: "Cambios" },
        { href: "#contact", label: "Contacto" }
      ],
      sections: [
        {
          id: "acceptance",
          eyebrow: "Acuerdo",
          title: "Aceptación de los Términos",
          paragraphs: [
            "Al descargar, instalar, acceder o usar cualquier aplicación móvil publicada por Gabg Dev, usted acepta estos Términos de Uso.",
            "Si no está de acuerdo con estos Términos, por favor no use las aplicaciones."
          ]
        },
        {
          id: "about",
          eyebrow: "Nuestras aplicaciones",
          title: "Sobre Nuestras Aplicaciones",
          paragraphs: [
            "Gabg Dev publica aplicaciones móviles diseñadas para uso personal, educativo, productividad, entretenimiento, información y otras experiencias de usuario.",
            "Estos Términos están destinados a aplicar a todas las aplicaciones móviles actuales y futuras publicadas por Gabg Dev, salvo que una aplicación específica proporcione términos separados."
          ]
        },
        {
          id: "intended-use",
          eyebrow: "Uso permitido",
          title: "Uso Permitido",
          paragraphs: [
            "Nuestras aplicaciones se ofrecen para uso personal y legal.",
            "Usted acepta usar las aplicaciones de forma responsable y no abusar, interrumpir, modificar, copiar, aplicar ingeniería inversa o interferir con el funcionamiento normal de las aplicaciones o servicios relacionados."
          ]
        },
        {
          id: "responsibilities",
          eyebrow: "Conducta del usuario",
          title: "Responsabilidades del Usuario",
          paragraphs: [
            "Usted es responsable del uso que haga de las aplicaciones y de cualquier información que envíe voluntariamente mediante formularios de comentarios, reportes de errores, sugerencias o solicitudes de soporte.",
            "Usted acepta no enviar contenido ilegal, abusivo, engañoso, dañino u ofensivo."
          ]
        },
        {
          id: "intellectual-property",
          eyebrow: "Titularidad",
          title: "Propiedad Intelectual",
          paragraphs: [
            "Todos los nombres de aplicaciones, diseños, interfaces, marcas, código fuente, gráficos y materiales relacionados pertenecen a Gabg Dev o a sus respectivos propietarios.",
            "Usted no puede copiar, distribuir, vender, modificar o reutilizar ninguna parte de las aplicaciones sin autorización, salvo cuando lo permita la ley o los términos de las tiendas de aplicaciones."
          ]
        },
        {
          id: "third-party-content",
          eyebrow: "Contenido de terceros",
          title: "Contenido de la Aplicación y de Terceros",
          paragraphs: [
            "Algunas aplicaciones pueden mostrar texto, audio, medios, referencias, datos u otro contenido que puede pertenecer o estar licenciado por terceros.",
            "Dicho contenido sigue siendo propiedad de sus respectivos titulares y puede estar sujeto a términos de copyright o licencia independientes.",
            "Los usuarios no deben redistribuir contenido de terceros desde las aplicaciones salvo que esté permitido por el titular de los derechos, la licencia correspondiente o los términos de la plataforma."
          ]
        },
        {
          id: "payments",
          eyebrow: "Soporte voluntario",
          title: "Donaciones y Pagos",
          paragraphs: [
            "Algunas aplicaciones pueden incluir opciones de donación voluntaria, como donaciones mediante PayPal u otros proveedores de pago.",
            "Las donaciones son opcionales y están destinadas a apoyar el desarrollo, alojamiento, mantenimiento y futuras mejoras.",
            "Salvo que se indique claramente lo contrario, las donaciones:"
          ],
          bullets: [
            "no otorgan derechos de propiedad;",
            "no desbloquean funciones premium garantizadas;",
            "no crean una suscripción;",
            "no garantizan funciones futuras;",
            "generalmente no son reembolsables."
          ],
          afterParagraphs: [
            "La información de pago es procesada por proveedores externos. Gabg Dev no almacena datos completos de tarjetas de crédito o débito."
          ]
        },
        {
          id: "ads",
          eyebrow: "Publicidad",
          title: "Anuncios",
          paragraphs: [
            "Algunas aplicaciones pueden mostrar anuncios mediante Google AdMob u otros servicios similares.",
            "El contenido de los anuncios, la personalización, medición e interacción con anuncios pueden ser gestionados por proveedores externos de publicidad conforme a sus propios términos y políticas de privacidad."
          ]
        },
        {
          id: "analytics",
          eyebrow: "Mejora de la aplicación",
          title: "Analíticas y Mejora de la Aplicación",
          paragraphs: [
            "Algunas aplicaciones pueden recopilar eventos de uso no sensibles para entender cómo se utilizan las funciones, mejorar el rendimiento, detectar errores y priorizar futuras mejoras.",
            "Ejemplos:"
          ],
          bullets: [
            "pantallas visitadas;",
            "funciones utilizadas;",
            "acciones de compartir;",
            "uso de audio;",
            "comentarios enviados;",
            "reportes de errores o fallos."
          ],
          afterParagraphs: [
            "Esta información se utiliza para mejorar la experiencia de la aplicación y se maneja conforme a nuestra Política de Privacidad."
          ]
        },
        {
          id: "external-links",
          eyebrow: "Terceros",
          title: "Enlaces Externos y Servicios de Terceros",
          paragraphs: [
            "Nuestras aplicaciones pueden contener enlaces a sitios web o servicios externos, incluyendo PayPal, Google, Apple, AWS, tiendas de aplicaciones, proveedores de anuncios u otras plataformas de terceros.",
            "Gabg Dev no es responsable del contenido, políticas, disponibilidad o prácticas de servicios de terceros.",
            "El uso de servicios de terceros está sujeto a sus propios términos y políticas de privacidad."
          ]
        },
        {
          id: "platform-rules",
          eyebrow: "Distribución",
          title: "Reglas de Tiendas y Plataformas",
          paragraphs: [
            "Al descargar o usar nuestras aplicaciones mediante Apple App Store, Google Play u otras plataformas de distribución, usted también acepta las reglas y términos aplicables de dichas plataformas."
          ]
        },
        {
          id: "availability",
          eyebrow: "Operación y cambios",
          title: "Disponibilidad y Cambios",
          paragraphs: [
            "Podemos actualizar, modificar, suspender o descontinuar cualquier aplicación o función en cualquier momento.",
            "No garantizamos que las aplicaciones estén siempre disponibles, libres de errores, sin interrupciones o compatibles con todos los dispositivos o versiones de sistemas operativos."
          ]
        },
        {
          id: "disclaimer",
          eyebrow: "Sin garantías",
          title: "Descargo de Responsabilidad",
          paragraphs: [
            "Las aplicaciones se ofrecen “tal cual” y “según disponibilidad”.",
            "En la medida máxima permitida por la ley, Gabg Dev no ofrece garantías sobre la disponibilidad, exactitud, confiabilidad, integridad o idoneidad de las aplicaciones.",
            "Las aplicaciones publicadas por Gabg Dev pueden incluir contenido educativo, informativo, de productividad, entretenimiento u otro contenido de aplicación. No sustituyen asesoría profesional, legal, médica, psicológica, financiera u otra asesoría calificada."
          ]
        },
        {
          id: "liability",
          eyebrow: "Límites legales",
          title: "Limitación de Responsabilidad",
          paragraphs: [
            "En la medida máxima permitida por la ley aplicable, Gabg Dev no será responsable por daños indirectos, incidentales, especiales, consecuentes o punitivos derivados del uso de las aplicaciones.",
            "Esto incluye, entre otros, pérdida de datos, problemas del dispositivo, interrupciones del servicio, pérdida de beneficios o imposibilidad de acceder a funciones."
          ]
        },
        {
          id: "termination",
          eyebrow: "Restricciones de acceso",
          title: "Terminación",
          paragraphs: [
            "Podemos restringir o descontinuar el acceso a ciertas funciones si consideramos que un usuario está abusando de la aplicación, violando estos Términos, intentando acceso no autorizado o interfiriendo con el servicio."
          ]
        },
        {
          id: "changes",
          eyebrow: "Actualizaciones",
          title: "Cambios a Estos Términos",
          paragraphs: [
            "Podemos actualizar estos Términos de Uso ocasionalmente.",
            "Cuando los actualicemos, modificaremos la fecha de “Última actualización” en esta página.",
            "El uso continuo de las aplicaciones después de los cambios significa que usted acepta los Términos actualizados."
          ]
        },
        {
          id: "contact",
          eyebrow: "Contacto",
          title: "Contacto",
          paragraphs: [
            "Si tiene preguntas sobre estos Términos de Uso, puede contactarnos en:",
            contactEmail
          ]
        }
      ],
      contact: null
    }
  }
};
